import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Image from "../components/elements/Image";
import Button from "../components/form/Button";
import NavigationPanel from "./NavigationPanel";
import Logo from "../assets/images/logoCube.png";
import { classNames } from "../helpers/classNames";
import { toast } from "react-toastify";
import LogoutModal from "../components/elements/LogoutModal";
import { useDispatch, useSelector } from "react-redux";
import { setAuthData } from "../store/slice/authSlice";
import { setTabSelection } from "../store/slice/selectedTabSlice";
import { fetchLanguageList } from "../service/languageService";
import { setLanguages } from "../store/slice/languageSlice";
import { appointmentLang, sideBar } from "../components/elements/language";
import ukFlag from "../assets/images/flag/uk.png";
import spainFlag from "../assets/images/flag/spain.png";
import italyFlag from "../assets/images/flag/italy.png";
import { getProfileDetails, logoutRequest, verifyToken } from "../service/authService";
import { setInitialProfileData } from "../store/slice/initialProfileSlice";
import { useSocket } from "../components/socket/SocketProvider";
import { isHardCallDisconnectReason } from "../util/callDisconnectReason";
import { isSelfLeaving } from "../util/callLeaveFlag";
import { resolveCallExitPath } from "../util/afterCallNavigate";
import { isLiveCallPath, resolveConsultationPath } from "../util/callPathHelpers";
import { setConsultantStatus, updateSingleConsultantStatus } from "../store/slice/consultantStatusSlice";
import { fetchConsultantStatusList } from "../service/consultantService";
import LanguageDropdown from "../components/elements/LanguageDropdown";
import usePageVisibility from "../util/PageVisibility";
import useOnlineStatus from "../util/OnlineStatus";
import { appointmentClose } from "../service/videoCallService";
import { apiRequest } from "../util/async/apiUtils";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import useInternetSpeed from "../components/elements/hooks/useInternetSpeed";
import { getDeviceId } from "../util/deviceIdentity";
import {
  bindRingtoneUnlock,
  bindRingtoneCrossTab,
  stopRingtone,
} from "../util/ringtone";
import { handleTeardownPresence, setManualOffline, clearAuthStorage } from "../util/consultantPresence";
import { updateProfileService } from "../service/profileService";
import {
  cleanIanaZone,
  getZoneOffsetMinutesForDate,
  isInstantAppointment,
  resolveAppointmentSchedule,
} from "../util/timezoneHelpers";
import moment from "moment-timezone";
import { personName } from "../util/callPeerDisplay";
import { appointmentDetails, fetchAppointmentList, generateVonageToken } from "../service/appointmentService";
import {
  isUserInAnotherActiveCall,
  ALREADY_IN_CALL_MESSAGE,
  forceAcquireCallLock,
  isOtherTabHoldingCall,
} from "../util/sessionTabLock";
import SecondResume from "../components/elements/SecondResume";
import PreCallDeviceCheck from "../components/elements/PreCallDeviceCheck";
import { saveCallDeviceReadiness } from "../util/deviceReadiness";
import JoinCallConfirmationModal from "../components/elements/JoinCallConfirmationModal";
import { JoinChatConfirmModal } from "../components/elements/JoinChatConfirmationModal";
import { setToken } from "../store/slice/vonageSlice";
import { setCallerData } from "../store/slice/callingSlice";

const langConfig = {
  joinCall: {
    en: "Join The Call",
    es: "Únase a la llamada",
    it: "Partecipa alla chiamata",
  },
};

export const isUpcomingAppointmentJoinable = (elm, userTz = "UTC") => {
  if (!elm) return false;
  if (isInstantAppointment(elm)) return false;

  const status = String(elm?.status || elm?.appointmentStatus || "").toLowerCase();
  // Under the "Upcoming" tab, status MUST be "active" (Upcoming tab excludes close, completed, inProgress, cancel, unCaptured)
  if (status !== "active") return false;

  const sched = resolveAppointmentSchedule(elm, userTz);
  if (!sched.isUpcoming) return false;

  const rawStartTime =
    sched.startUtcIso ||
    elm?.appointmentData?.details?.appointmentStartTime ||
    elm?.details?.appointmentStartTime ||
    elm?.appointmentStartTime;

  const rawEndTime =
    sched.actualEndUtcIso ||
    sched.endUtcIso ||
    elm?.appointmentData?.details?.appointmentEndTime ||
    elm?.details?.appointmentEndTime ||
    elm?.appointmentEndTime;

  if (!rawStartTime) return false;

  const now = moment.utc();
  const startTime = moment.utc(rawStartTime);
  if (!startTime.isValid()) return false;

  const endTime = rawEndTime ? moment.utc(rawEndTime) : moment.utc(startTime).add(60, "minutes");
  if (!endTime.isValid()) return false;

  // The "Join the Call" button turns green if and only if now is between startTime and endTime
  return now.isSameOrAfter(startTime) && now.isSameOrBefore(endTime);
};

export const isScheduledAppointmentLive = isUpcomingAppointmentJoinable;

const DefaultHeader = ({ headerClass }) => {
  const [loaded, setLoaded] = useState(true);
  const token = localStorage.getItem("auth_token") || Cookies.get("auth_token");
  const localProfileData = JSON.parse(localStorage.getItem("userDetails"));
  const userId = localProfileData?._id;
  const [scrolled, setScrolled] = useState(false);
  const [menuToggle, setMenuToggle] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [languageList, setLanguageList] = useState([]);
  const lang = useSelector((state) => state?.languageSlice?.data);
  const language = localStorage.getItem("language") || lang;
  const profile = useSelector((state) => state?.initialProfileSlice?.data);
  // Incoming call/chat modals live in IncomingCallHost (global) — not here.
  // DefaultHeader remounts per route; global host stays mounted on every page.
  const callerData = useSelector((state) => state?.callingSlice?.data);
  const dispatch = useDispatch();

  const socket = useSocket();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isJoinCall, setIsJoinCall] = useState(false);
  const localAppointmentData = JSON.parse(localStorage.getItem("appointmentData"));
  const [appointmenData, setAppointmenData] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("appointmentData") || "null");
      const currentTz = cleanIanaZone(
        localProfileData?.personal?.timeZone?.zoneName ||
        (typeof moment?.tz?.guess === "function" ? moment.tz.guess() : "UTC")
      );
      if (stored?.appointmentId && !isInstantAppointment(stored)) {
        const storedStatus = String(stored?.status || stored?.appointmentStatus || "").toLowerCase();
        if (storedStatus === "inprogress") {
          return stored;
        }
        if (localProfileData?.roleCode !== "consultant" && isUpcomingAppointmentJoinable(stored, currentTz)) {
          return stored;
        }
        localStorage.removeItem("appointmentData");
        localStorage.removeItem("joined");
        localStorage.removeItem("callPageLeaveFlag");
        return null;
      }
    } catch {
      return null;
    }
    return null;
  });
  const roleCode = JSON.parse(localStorage.getItem("userDetails"))?.roleCode;
  const [loading, setLoading] = useState(false);
  const { speed, error, checkInternetSpeed } = useInternetSpeed();
  useEffect(() => {
    checkInternetSpeed();
  }, []);
  const [toastShown, setToastShown] = useState(false);
  const [secondModalOpen, setSecondModalOpen] = useState(false);
  const [showDeviceCheck, setShowDeviceCheck] = useState(false);
  const [callConfirmModal, setCallConfirmModal] = useState(false);
  const [isOpenConfirmationChatModal, setIsOpenConfirmationChatModal] = useState(false);
  const [appointmentDetailsData, setAppointmentDetailsData] = useState(null);
  const startCallInFlightRef = useRef(false);

  useEffect(() => {
    if (
      !callConfirmModal &&
      !isOpenConfirmationChatModal &&
      !loading &&
      !showDeviceCheck &&
      !secondModalOpen
    ) {
      startCallInFlightRef.current = false;
    }
  }, [callConfirmModal, isOpenConfirmationChatModal, loading, showDeviceCheck, secondModalOpen]);

  // Unlock mobile audio + multi-tab ring stop (actual play is owned by IncomingCallHost)
  useEffect(() => {
    bindRingtoneUnlock();
    bindRingtoneCrossTab();
  }, []);

  const [appointmentProfile, setAppointmentProfile] = useState(null);
  const [onCall, setOnCall] = useState(false);
    const navigate = useNavigate();
  //---------------Notification Loader----------------------
  const [notificationToggle, setNotificationToggle] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(1);
  const [notificationData, setNotificationData] = useState([]);
  const [totalNotificationLength, setTotalNotificationLength] = useState(null);
  const [notificationLoader, setNotificationLoader] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [totalUnseenNotification, setTotalUnseenNotification] = useState(null);
  const [seenLoader, setSeenLoader] = useState(false);
  const [disconnectedProfile, setDisconnectedProfile] = useState(null);
  const gmtOffset = localProfileData?.personal?.timeZone?.gmtOffsetName;
  let joinStatus = localStorage.getItem("joined") || false;

  const onlineHit = useCallback(() => {
    if (userId && socket) {
      const manualOffline = localStorage.getItem("manualOfflineStatus") === "offline";
      // If consultant chose Offline, re-assert offline — never push online on refresh
      if (manualOffline) {
        if (socket.connected) socket.emit("userOffline", userId);
        const handleConnect = () => socket.emit("userOffline", userId);
        socket.on("connect", handleConnect);
        return () => socket.off("connect", handleConnect);
      }
      if (socket.connected) {
        socket.emit("userOnline", userId);
      }
      const handleConnect = () => {
        if (localStorage.getItem("manualOfflineStatus") === "offline") {
          socket.emit("userOffline", userId);
          return;
        }
        socket.emit("userOnline", userId);
      };
      socket.on("connect", handleConnect);
      return () => socket.off("connect", handleConnect);
    }
  }, [socket, userId]);

  useEffect(() => {
    const cleanup = onlineHit();
    return () => {
      if (cleanup) cleanup();
    };
  }, [onlineHit]);

  useEffect(() => {
    if (!token) {
      clearAuthStorage();
    }
  }, [token, navigate]);

  // Do NOT unlock a separate silent Audio element — iOS only unlocks the same
  // element / AudioContext used later. bindRingtoneUnlock handles that globally.

  function convertGmtOffsetToMinutes(offsetStr) {
    const cleaned = (offsetStr ?? "").replace(/^(UTC|GMT)/i, "");

    const match = cleaned.match(/^([+-])(\d{2})(?::?(\d{2}))?$/);
    if (!match) {
      try {
        const zoneName = localProfileData?.personal?.timeZone?.zoneName;
        if (zoneName && String(zoneName).includes("/")) {
          return -getZoneOffsetMinutesForDate(zoneName);
        }
      } catch (_) { }
      return new Date().getTimezoneOffset();
    }

    const sign = match[1] === "-" ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = match[3] ? parseInt(match[3], 10) : 0;

    return sign * (hours * 60 + minutes);
  }

  const fetchNotifications = useCallback(async () => {
    setNotificationLoader(true);
    try {
      const res = await apiRequest("get", "/notification/list", {
        queries: {
          offset: convertGmtOffsetToMinutes(gmtOffset), //new Date().getTimezoneOffset(),
          limit: 10,
          page,
        },
      });

      if (res?.data?.docs?.status === 200) {
        setNotificationData((prevList) => (page === 1 ? res.data.docs.data : [...prevList, ...res.data.docs.data]));

        setHasNextPage(res.data.docs.metadata.hasNextPage);
        setTotalNotificationLength(res.data.docs.metadata.totalDocs);
        setTotalUnseenNotification(res.data.docs.metadata.unseen);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setNotificationLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    token && fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const [allSeenLoader, setAllSeenLoader] = useState(false);
  const handleAllSeenNotification = async () => {
    try {
      setAllSeenLoader(true);
      let res = await apiRequest("put", "/notification/update", {
        body: {
          id: [],
        },
      });
      setAllSeenLoader(false);
      if (res?.data?.status === 200) {
        setSelectedNotifications([]);
        toast.success(res?.data?.message, { autoClose: 3000 });
        fetchNotifications();
      } else {
        toast.error(res?.data?.message, { autoClose: 3000 });
      }
    } catch (error) {
      setAllSeenLoader(false);
    }
  };

  const UserId =
    roleCode === "consultant" ? appointmenData?.callerData?.user?.id : appointmenData?.callerData?.consultant?.id;

  const matchedId =
    roleCode === "consultant" ? appointmenData?.callerData?.consultant?.id : appointmenData?.callerData?.user?.id;

  const pageLeave = useCallback(async () => {
    if (!localStorage.getItem("callPageLeaveFlag")) return;
    if (!UserId || !appointmenData?.appointmentId) return;
    if (matchedId !== userId) return;
    try {
      await apiRequest("post", "appointment/already-in-call", {
        body: {
          userId: UserId,
          appointmentId: appointmenData.appointmentId,
          disconnect: "true",
          deviceId: getDeviceId(),
        },
      });
      localStorage.removeItem("callPageLeaveFlag");
    } catch (error) {
      console.error("Error disconnecting call:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UserId, appointmenData, matchedId, userId]);

  useEffect(() => {
    if (appointmenData) {
      pageLeave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLeave]);

  useSocket(
    "userDisconnectFromCallNotifyResponse",
    (data) => {
      data && data?.userId === localProfileData?._id && setDisconnectedProfile(data);
    },
    [socket],
  );

  const handleLogOut = () => {
    const targetId = userId || profile?._id || localProfileData?._id;
    const currentToken = token || localStorage.getItem("auth_token") || Cookies.get("auth_token");

    // 1. Immediately notify socket of offline status
    if (socket && targetId) {
      try {
        socket.emit("userOffline", targetId);
        socket.emit("userOffline");
      } catch (_) { }
    } else if (socket) {
      try {
        socket.emit("userOffline");
      } catch (_) { }
    }

    // 2. Set manual offline flag
    setManualOffline(true);

    // 3. Update DB to offline before wiping token
    if (currentToken) {
      try {
        updateProfileService({ onlineStatus: "offline" });
      } catch (_) { }
      if (typeof fetch !== "undefined") {
        try {
          const BASE_URL = process.env.REACT_APP_API_URL || "https://dev.api.prontopsy.com/api/v1";
          const formData = new FormData();
          formData.append("onlineStatus", "offline");
          fetch(`${BASE_URL}/user/edit`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${currentToken}` },
            body: formData,
            keepalive: true,
          }).catch(() => { });
        } catch (_) { }
      }
    }

    // 4. Send API logout request
    logoutRequest();

    // 5. Close appointment if any
    if (appointmenData && appointmenData?.appointmentId) {
      appointmentClose({ appointmentId: appointmenData?.appointmentId }).then((res) => {
        try {
          socket &&
            socket.emit("videoSessionDisconnect", {
              disconnect: true,
              appointmentId: appointmenData?.appointmentId,
            });

          socket &&
            socket.emit("chatDisconnect", {
              disconnect: true,
              appointmentId: appointmenData?.appointmentId,
            });

          socket &&
            localProfileData?.roleCode === "consultant" &&
            socket.emit("consultantBusy", {
              consultantId: localProfileData?._id,
              userId: appointmenData?.callerData?.user?.id,
              isBusy: false,
            });

          socket &&
            socket.emit("userDisconnectFromCallNotify", {
              disconnected: false,
              sessionId: appointmenData.sessionId,
              userId: localProfileData?._id,
              appointmentId: appointmenData?.appointmentId,
            });
        } catch (_) { }
      });
    }

    // 6. Clear auth storage
    dispatch(setAuthData({}));
    localStorage.removeItem("auth_token");
    Cookies.remove("auth_token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("appointmentData");
    localStorage.removeItem("joined");

    // 7. Disconnect socket cleanly
    try {
      socket?.disconnect();
    } catch (_) { }

    toast.success("You are logged out!");
    setLoaded(false);
    setTimeout(() => {
      setShowModal(false);
      setLoaded(true);
      window.location.href = "/signin";
    }, 500);
  };

  useSocket("chatDisconnectResponse", async (data) => {
    if (!data?.disconnect) return;
    const myApptId = localAppointmentData?.appointmentId;
    if (!myApptId || String(data?.appointmentId) !== String(myApptId)) return;

    if (isSelfLeaving(data?.appointmentId)) return;

    const reason = String(data?.reason || "");
    const hardEnd = isHardCallDisconnectReason(reason) || data?.serverClosed === true;
    if (!hardEnd) return;

    socket &&
      localProfileData?.roleCode === "consultant" &&
      socket.emit("consultantBusy", {
        consultantId: localProfileData?._id,
        userId: localAppointmentData?.consultantId,
        isBusy: false,
      });
    localStorage.removeItem("joined");
    localStorage.removeItem("appointmentData");
    setAppointmenData(null);
  });

  const fetchProfileDetails = useCallback(() => {
    if (!token) return;
    getProfileDetails(token).then((response) => {
      if (response?.data?.status === 200) {
        if (!response?.data?.docs?.appointmentData?._id) {
          setAppointmenData(null);
        }

        // Incoming ring UI is owned by IncomingCallHost (global).
        // Here we only keep local appointment/header state in sync.
        const appt = response?.data?.docs?.appointmentData;
        const ringing = response?.data?.docs?.ringingStatus;
        const apptStatus = appt?.status;
        const RING_MAX_AGE_MS = 90 * 1000;
        const apptFresh =
          appt?.createdAt || appt?.updatedAt
            ? Date.now() - new Date(appt.updatedAt || appt.createdAt).getTime() < RING_MAX_AGE_MS
            : false;

        if (
          roleCode === "consultant" &&
          ringing?.status === true &&
          appt?.consultantStatus === "notJoin" &&
          (apptStatus === "active" || apptStatus === "inProgress") &&
          apptFresh
        ) {
          const Data = {
            user: {
              name:
                appt?.clientDetails?.firstName + " " + appt?.clientDetails?.lastName,
              email: appt?.clientDetails?.email,
              image: appt?.clientDetails?.image,
              id: appt?.clientDetails?._id,
            },
            consultant: {
              name:
                appt?.consultantDetails?.firstName +
                " " +
                appt?.consultantDetails?.lastName,
              email: appt?.consultantDetails?.email,
              image: appt?.consultantDetails?.image,
              id: appt?.consultantDetails?._id,
            },
            mode: appt?.service?.mode?.slug,
          };
          const finalData = {
            appointmentId: appt?._id,
            consultantId: appt?.consultantId,
            clientId: appt?.clientId,
            appointmentStartTime: appt?.consultantJoinTime || Date.now(),
            mode: appt?.service?.mode?.slug,
            callerData: { ...Data },
            user: Data.user,
            consultant: Data.consultant,
            sessionId: appt?.sessionId,
            token: appt?.sessionToken,
            paymentMode: appt?.paymentMode,
          };
          if (finalData.appointmentId) {
            setAppointmentProfile(finalData);
            setAppointmenData(finalData);
            localStorage.setItem("appointmentData", JSON.stringify(finalData));
          }
        } else if (
          ringing?.status === true &&
          (!apptFresh || !["active", "inProgress"].includes(apptStatus))
        ) {
          stopRingtone();
          localStorage.removeItem("appointmentData");
          localStorage.removeItem("callPageLeaveFlag");
          localStorage.removeItem("joined");
        } else if (
          appt?.consultantStatus === "notJoin" &&
          ringing?.status === false
        ) {
          socket &&
            socket.emit("consultantBusy", {
              consultantId: appt?.consultantDetails?._id || "",
              userId: appt?.clientDetails?._id,
              isBusy: false,
            });
          localStorage.removeItem("appointmentData");
          localStorage.removeItem("callPageLeaveFlag");
          localStorage.removeItem("joined");
        }

        if (response?.data?.docs?._id) {
          localStorage.setItem("userDetails", JSON.stringify(response?.data?.docs));
          dispatch(setInitialProfileData(response.data.docs));
        }
        if (
          response?.data?.docs?.appointmentData?._id &&
          (response?.data?.docs?.appointmentData?.status === "inProgress" ||
            response?.data?.docs?.appointmentData?.status === "active")
        ) {
          const existingLocal = JSON.parse(localStorage.getItem("appointmentData") || "{}");
          const appointment = {
            appointmentId: response?.data?.docs?.appointmentData?._id,
            appointmentStartTime: response?.data?.docs?.appointmentData?.details?.appointmentStartTime,
            callerData: {
              consultant: {
                id: response?.data?.docs?.appointmentData?.consultantId,
                image: response?.data?.docs?.appointmentData?.consultantDetails?.image,
                name:
                  response?.data?.docs?.appointmentData?.consultantDetails?.firstName +
                  " " +
                  response?.data?.docs?.appointmentData?.consultantDetails?.lastName,
              },
              user: {
                id: response?.data?.docs?.appointmentData?.clientId,
                image: response?.data?.docs?.appointmentData?.clientDetails?.image,
                name:
                  response?.data?.docs?.appointmentData?.clientDetails?.firstName +
                  " " +
                  response?.data?.docs?.appointmentData?.clientDetails?.lastName,
              },
            },
            consultantId: response?.data?.docs?.appointmentData?.consultantId,
            mode: response?.data?.docs?.appointmentData?.service?.mode?.slug,
            paymentMode: response?.data?.docs?.appointmentData?.paymentMode,
            // Preserve local session credentials if profile briefly omits them
            sessionId:
              response?.data?.docs?.appointmentData?.sessionId || existingLocal?.sessionId || null,
            token:
              response?.data?.docs?.appointmentData?.sessionToken || existingLocal?.token || null,
          };
          appointment.appointmentId && setAppointmentProfile(appointment);
          appointment.appointmentId && setAppointmenData(appointment);
          appointment.appointmentId && localStorage.setItem("appointmentData", JSON.stringify(appointment));
        } else {
          // Do not wipe an in-progress local call just because profile has no appointment yet
          const onCallPage =
            typeof window !== "undefined" && isLiveCallPath(window.location?.pathname);
          const currentStored = (() => {
            try {
              return JSON.parse(localStorage.getItem("appointmentData") || "null");
            } catch {
              return null;
            }
          })();
          const currentTz = cleanIanaZone(
            localProfileData?.personal?.timeZone?.zoneName ||
            (typeof moment?.tz?.guess === "function" ? moment.tz.guess() : "UTC")
          );
          const storedStatus = String(currentStored?.status || currentStored?.appointmentStatus || "").toLowerCase();
          const isInProgress = storedStatus === "inprogress";
          const isJoinableUpcoming = roleCode !== "consultant" && isUpcomingAppointmentJoinable(currentStored, currentTz);
          const isScheduledInProgress =
            currentStored?.appointmentId &&
            !isInstantAppointment(currentStored) &&
            (isInProgress || isJoinableUpcoming);

          if (isScheduledInProgress && !appointmenData) {
            setAppointmenData(currentStored);
          } else if (!onCallPage && !isScheduledInProgress) {
            socket &&
              socket.emit("consultantBusy", {
                consultantId: appointmenData?.callerData?.consultant?.id || "",
                userId: appointmenData?.callerData?.user?.id,
                isBusy: false,
              });
            localStorage.removeItem("appointmentData");
            localStorage.removeItem("callPageLeaveFlag");
            localStorage.removeItem("joined");
          }
        }
      } else if (response?.status === 401 || response?.data?.status === 401) {
        localStorage.removeItem("auth_token");
        Cookies.remove("auth_token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("userDetails");
        localStorage.removeItem("appointmentData");
        localStorage.removeItem("joined");
        dispatch(setAuthData({}));
        dispatch(setInitialProfileData(null));
        toast.error("Your session has expired. Please login again.", {
          autoClose: 5000,
        });
        navigate("/signin", { replace: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dispatch, roleCode]);

  const fetchStatusList = useCallback(() => {
    fetchConsultantStatusList().then((res) => {
      dispatch(setConsultantStatus(res?.data?.data || []));
    });
  }, [dispatch]);

  const userTz = cleanIanaZone(
    localProfileData?.personal?.timeZone?.zoneName ||
    (typeof moment?.tz?.guess === "function" ? moment.tz.guess() : "UTC")
  );

  const [liveTick, setLiveTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setLiveTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchActiveScheduledAppointment = useCallback(async () => {
    if (!token) return;
    try {
      const currentTz = cleanIanaZone(
        localProfileData?.personal?.timeZone?.zoneName ||
        (typeof moment?.tz?.guess === "function" ? moment.tz.guess() : "UTC")
      );

      const res = await fetchAppointmentList(token, {
        "status[0]": "inProgress",
        "status[1]": "active",
        limit: 50,
        sortQuery: "-details.appointmentStartTime",
      });
      const resData =
        res?.data?.docs?.data ||
        res?.data?.data ||
        (Array.isArray(res?.data?.docs) ? res.data.docs : []);

      if (Array.isArray(resData) && resData.length > 0) {
        // Priority 1: Any appointment that is currently inProgress (ongoing active consultation)
        const inProgressAppt = resData.find((elm) => {
          if (!elm || isInstantAppointment(elm)) return false;
          const status = String(elm?.status || elm?.appointmentStatus || "").toLowerCase();
          return status === "inprogress";
        });

        // Priority 2: Upcoming scheduled appointment that is joinable right now (within time window)
        // NOTE: For upcoming scheduled appointments, ONLY the CLIENT can join!
        // The consultant is "Waiting for Client" and cannot initiate or join before the client calls.
        const joinableUpcomingAppt = roleCode === "consultant"
          ? null
          : resData.find((elm) => isUpcomingAppointmentJoinable(elm, currentTz));

        const scheduledAppt = inProgressAppt || joinableUpcomingAppt;

        if (scheduledAppt) {
          const clientData = scheduledAppt?.clientData || scheduledAppt?.clientDetails;
          const consultantData = scheduledAppt?.consultantData || scheduledAppt?.consultantDetails;
          const existingLocal = (() => {
            try {
              return JSON.parse(localStorage.getItem("appointmentData") || "{}");
            } catch {
              return {};
            }
          })();
          const apptStatus = scheduledAppt?.status || scheduledAppt?.appointmentStatus || "active";
          const normalized = {
            appointmentId: scheduledAppt._id || scheduledAppt.id,
            status: apptStatus,
            consultantId: scheduledAppt.consultantId || consultantData?._id,
            clientId: scheduledAppt.clientId || clientData?._id,
            appointmentStartTime:
              scheduledAppt?.details?.appointmentStartTime ||
              scheduledAppt.appointmentStartTime ||
              existingLocal?.appointmentStartTime ||
              Date.now(),
            appointmentEndTime:
              scheduledAppt?.details?.appointmentEndTime ||
              scheduledAppt.appointmentEndTime ||
              existingLocal?.appointmentEndTime,
            details: scheduledAppt.details || existingLocal?.details,
            service: scheduledAppt.service || existingLocal?.service,
            consultantData: consultantData || existingLocal?.consultantData,
            clientData: clientData || existingLocal?.clientData,
            rate: {
              value:
                scheduledAppt?.service?.price ||
                scheduledAppt?.details?.price ||
                existingLocal?.rate?.value ||
                existingLocal?.service?.price ||
                0,
              currency:
                scheduledAppt?.details?.currency ||
                consultantData?.currencyData?.currency ||
                existingLocal?.rate?.currency ||
                "",
            },
            mode: scheduledAppt?.service?.mode?.slug || scheduledAppt.mode || existingLocal?.mode || "video",
            callerData: {
              user: {
                id: clientData?._id || existingLocal?.callerData?.user?.id,
                name:
                  personName(clientData) ||
                  `${clientData?.firstName || ""} ${clientData?.lastName || ""}`.trim() ||
                  existingLocal?.callerData?.user?.name ||
                  "",
                image: clientData?.image || existingLocal?.callerData?.user?.image,
                email: clientData?.email || existingLocal?.callerData?.user?.email,
              },
              consultant: {
                id: consultantData?._id || existingLocal?.callerData?.consultant?.id,
                name:
                  personName(consultantData) ||
                  `${consultantData?.firstName || ""} ${consultantData?.lastName || ""}`.trim() ||
                  existingLocal?.callerData?.consultant?.name ||
                  "",
                image: consultantData?.image || existingLocal?.callerData?.consultant?.image,
                email: consultantData?.email || existingLocal?.callerData?.consultant?.email,
              },
            },
            sessionId: scheduledAppt.sessionId || existingLocal?.sessionId || null,
            token: scheduledAppt.sessionToken || existingLocal?.token || null,
            sessionToken: scheduledAppt.sessionToken || existingLocal?.sessionToken || null,
            paymentMode: scheduledAppt.paymentMode || existingLocal?.paymentMode,
                      };
          setAppointmenData(normalized);
          localStorage.setItem("appointmentData", JSON.stringify(normalized));
          return;
        }
      }

      const localAppt = (() => {
        try {
          return JSON.parse(localStorage.getItem("appointmentData") || "null");
        } catch {
          return null;
        }
      })();
      if (localAppt?.appointmentId && !isInstantAppointment(localAppt)) {
        const apptStatus = String(localAppt?.status || localAppt?.appointmentStatus || "").toLowerCase();
        if (apptStatus === "inprogress") {
          // Keep active in-progress call
        } else if (roleCode === "consultant" || !isUpcomingAppointmentJoinable(localAppt, currentTz)) {
          setAppointmenData(null);
          localStorage.removeItem("appointmentData");
          localStorage.removeItem("joined");
          localStorage.removeItem("callPageLeaveFlag");
        }
      } else if (!localAppt) {
        setAppointmenData(null);
      }
    } catch (_) {}
  }, [token, localProfileData?.personal?.timeZone?.zoneName]);

  const startInitiateCall = useCallback(
    async (appointmentId) => {
      if (startCallInFlightRef.current) return;
      startCallInFlightRef.current = true;
      setLoading(true);

      try {
        forceAcquireCallLock(appointmentId);

        // 1. Authenticate appointment (same as authenticateAppointment in AppointmentItem.js)
        const authRes = await apiRequest("post", "appointment/authenticate-appointment", {
          body: { appointmentId },
        });

        if (
          [401, 403, 404].includes(authRes?.status) ||
          authRes?.data?.data?.appointmentStatus === "close"
        ) {
          toast.error("Appointment closed or not found.");
          startCallInFlightRef.current = false;
          setLoading(false);
          return;
        }

        // 2. Generate token using generateVonageToken (exact same as handleGenerateToken in AppointmentItem.js)
        const currentLocalUser = (() => {
          try {
            return JSON.parse(localStorage.getItem("userDetails") || "{}");
          } catch {
            return localProfileData || {};
          }
        })();
        const defaultCardId = currentLocalUser?.cardsDetails?.[0]?.id;
        const res = await generateVonageToken({ id: appointmentId, cardId: defaultCardId });

        if (res?.data?.status === 200 && res?.data?.data?._id) {
          const doc = res.data.data;
          const targetConsultantId = String(
            doc?.consultantId?._id ||
            doc?.consultantId?.id ||
            (typeof doc?.consultantId === "string" ? doc?.consultantId : "") ||
            doc?.consultantData?._id ||
            doc?.consultantDetails?._id ||
            appointmenData?.consultantId ||
            ""
          );
          const targetClientId = String(
            doc?.clientId?._id ||
            doc?.clientId?.id ||
            (typeof doc?.clientId === "string" ? doc?.clientId : "") ||
            doc?.clientData?._id ||
            doc?.clientDetails?._id ||
            appointmenData?.clientId ||
            ""
          );

          const consultantName =
            personName(doc?.consultantData) ||
            personName(doc?.consultantDetails) ||
            `${doc?.consultantData?.firstName || ""} ${doc?.consultantData?.lastName || ""}`.trim() ||
            appointmenData?.callerData?.consultant?.name ||
            "";
          const clientName =
            personName(doc?.clientData) ||
            personName(doc?.clientDetails) ||
            `${doc?.clientData?.firstName || ""} ${doc?.clientData?.lastName || ""}`.trim() ||
            appointmenData?.callerData?.user?.name ||
            "";

          const appointment_data = {
            appointmentId: doc?._id,
            consultantId: targetConsultantId,
            clientId: targetClientId,
            appointmentStartTime: doc?.details?.appointmentStartTime || appointmenData?.appointmentStartTime,
            appointmentEndTime: doc?.details?.appointmentEndTime || appointmenData?.appointmentEndTime,
            details: doc?.details || appointmenData?.details,
            service: doc?.service || appointmenData?.service,
            waitingArray: doc?.waitingArray,
            activeArray: doc?.activeArray,
            callerData: {
              consultant: {
                id: targetConsultantId,
                image:
                  doc?.consultantData?.image?.url ||
                  doc?.consultantData?.image ||
                  appointmenData?.callerData?.consultant?.image,
                name: consultantName,
              },
              user: {
                id: targetClientId,
                image:
                  doc?.clientData?.image?.url ||
                  doc?.clientData?.image ||
                  appointmenData?.callerData?.user?.image,
                name: clientName,
              },
            },
            mode: doc?.service?.mode?.slug || appointmenData?.mode || "video",
            paymentMode: doc?.paymentMode || "stripe",
            sessionId: doc?.sessionId || appointmenData?.sessionId,
            token: doc?.sessionToken || appointmenData?.token || appointmenData?.sessionToken,
          };

          setAppointmentDetailsData(appointment_data);
          setAppointmenData(appointment_data);
          localStorage.setItem("appointmentData", JSON.stringify(appointment_data));
          localStorage.setItem("consultantId", JSON.stringify(targetConsultantId));
          localStorage.setItem("joined", "true");
          sessionStorage.removeItem(`prontopsy_rejoin_wait_${doc?._id}`);
          if (!localStorage.getItem(`actualConnectedSeconds_${doc?._id}`)) {
            localStorage.setItem(`actualConnectedSeconds_${doc?._id}`, "0");
          }
          try {
            localStorage.removeItem("actualConnectedSeconds");
          } catch (_) {}

          const apptId = doc?._id;
          if (socket && apptId) {
            socket.emit("joinAppointmentRoom", { appointmentId: apptId });
            socket.emit("consultantBusy", {
              consultantId: targetConsultantId,
              userId: targetClientId,
              isBusy: true,
              appointmentId: apptId,
            });
          }

          const callMode = doc?.service?.mode?.slug || appointment_data.mode;
          if (callMode === "chat") {
            dispatch(setCallerData(appointment_data.callerData));
            if (socket) {
              socket.emit("chatSessionIndirect", {
                ...appointment_data,
                consultantId: targetConsultantId,
                clientId: targetClientId,
                callerData: appointment_data.callerData,
              });
              socket.emit("chatSession", {
                ...appointment_data,
                consultantId: targetConsultantId,
                clientId: targetClientId,
                callerData: appointment_data.callerData,
              });
            }
            setIsOpenConfirmationChatModal(true);
          } else {
            dispatch(setCallerData(appointment_data.callerData));
            if (socket) {
              socket.emit("consultantIndirectCall", {
                ...appointment_data.callerData,
                ...appointment_data,
                consultantId: targetConsultantId,
                clientId: targetClientId,
                callerData: appointment_data.callerData,
              });
              socket.emit("consultantCall", {
                ...appointment_data.callerData,
                ...appointment_data,
                consultantId: targetConsultantId,
                clientId: targetClientId,
                callerData: appointment_data.callerData,
              });
              socket.emit("vonageTokBoxSession", {
                ...appointment_data,
                consultantId: targetConsultantId,
                clientId: targetClientId,
              });
            }

            dispatch(
              setToken({
                loading: true,
                mode: callMode,
                sessionId: doc?.sessionId,
                token: doc?.sessionToken,
              }),
            );
            setCallConfirmModal(true);
          }
        } else if (res?.data?.status === 403) {
          toast.error(res?.data?.message, { autoClose: 4000 });
          startCallInFlightRef.current = false;
        } else {
          toast.info(res?.data?.message || "This appointment cannot be started at this moment.", {
            autoClose: 3000,
          });
          startCallInFlightRef.current = false;
        }
      } catch (err) {
        console.error("Error initiating appointment from header:", err);
        toast.error(
          err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          "Failed to initiate call",
          { autoClose: 3000 }
        );
        startCallInFlightRef.current = false;
      } finally {
        setLoading(false);
      }
    },
    [appointmenData, socket, dispatch, localProfileData],
  );

  const handleJoinActiveCall = useCallback(async () => {
    if (!appointmenData?.appointmentId) return;
    const appointmentId = appointmenData.appointmentId;
    const isApptInProgress = String(appointmenData?.status || appointmenData?.appointmentStatus || "").toLowerCase() === "inprogress";

    if (
      startCallInFlightRef.current ||
      loading ||
      secondModalOpen ||
      showDeviceCheck ||
      callConfirmModal ||
      isOpenConfirmationChatModal
    ) {
      return;
    }

    if (speed && speed * 8 < 8) {
      toast.info(
        "Slow internet connection. Please ensure your connection is stable before starting the consultation.",
        { autoClose: 6000 },
      );
      return;
    }

    if (isUserInAnotherActiveCall(appointmentId)) {
      toast.error(ALREADY_IN_CALL_MESSAGE, {
        autoClose: 5000,
        toastId: "already-in-active-call",
      });
      return;
    }

    if (isOtherTabHoldingCall(appointmentId)) {
      toast.error("This consultation is already open in another window.", {
        autoClose: 5000,
        toastId: "call-lock-other-tab",
      });
      return;
    }

    // CASE 1: If appointment is already inProgress, rejoin directly without repeating client consent/payment flow
    if (isApptInProgress) {
      forceAcquireCallLock(appointmentId);
      localStorage.setItem("joined", "true");
      const mode = appointmenData?.mode || appointmenData?.service?.mode?.slug || "video";
      const targetPath = resolveConsultationPath(appointmentId, mode);
      navigate(targetPath);
      return;
    }

    // CASE 2: Upcoming scheduled appointment (client flow):
    try {
      appointmentDetails({ id: appointmentId })
        .then((apptRes) => {
          if (apptRes?.data?.status === 200 && apptRes?.data?.docs) {
            const fresh = apptRes.data.docs;
            const freshConsultant = fresh?.consultantData || fresh?.consultantDetails;
            setAppointmenData((prev) => ({
              ...prev,
              details: fresh?.details || prev?.details,
              service: fresh?.service || prev?.service,
              consultantData: freshConsultant || prev?.consultantData,
              rate: {
                value: fresh?.service?.price || fresh?.details?.price || prev?.rate?.value || 0,
                currency:
                  fresh?.details?.currency ||
                  freshConsultant?.currencyData?.currency ||
                  prev?.rate?.currency ||
                  "",
              },
            }));
          }
        })
        .catch(() => {});
    } catch (_) {}

    // 1. Open screenshot popup (SecondResume)
    setSecondModalOpen(true);
  }, [
    appointmenData,
    loading,
    secondModalOpen,
    showDeviceCheck,
    callConfirmModal,
    isOpenConfirmationChatModal,
    speed,
    navigate,
  ]);

  useSocket("appointmentStatusChanged", (data) => {
    const status = String(data?.status || data?.appointmentStatus || "").toLowerCase();
    if (["close", "completed", "cancel", "uncaptured"].includes(status)) {
      if (
        !data?.appointmentId ||
        String(data.appointmentId) === String(appointmenData?.appointmentId)
      ) {
        setIsJoinCall(false);
        setAppointmenData(null);
        localStorage.removeItem("appointmentData");
        localStorage.removeItem("joined");
      }
    } else {
      fetchActiveScheduledAppointment();
    }
  });

  useSocket("callRejoinTimeoutResponse", (data) => {
    if (
      !data?.appointmentId ||
      String(data.appointmentId) === String(appointmenData?.appointmentId)
    ) {
      setIsJoinCall(false);
      setAppointmenData(null);
      localStorage.removeItem("appointmentData");
      localStorage.removeItem("joined");
    }
  });

  useSocket("indirectAppointmentConfirmJoinResponse", () => {
    fetchActiveScheduledAppointment();
  });

  useSocket("consultantJoinResponse", () => {
    fetchActiveScheduledAppointment();
  });

  useSocket("chatJoinConfirmResponse", () => {
    fetchActiveScheduledAppointment();
  });

  useEffect(() => {
    fetchProfileDetails();
    fetchStatusList();
    fetchActiveScheduledAppointment();
    const interval = setInterval(() => {
      fetchStatusList();
      fetchActiveScheduledAppointment();
    }, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStatusList, fetchProfileDetails, fetchActiveScheduledAppointment]);

  useEffect(() => {
    const handleVisibilitySync = () => {
      if (document.visibilityState === "visible") {
        fetchActiveScheduledAppointment();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilitySync);
    window.addEventListener("focus", handleVisibilitySync);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilitySync);
      window.removeEventListener("focus", handleVisibilitySync);
    };
  }, [fetchActiveScheduledAppointment]);

  useEffect(() => {
    if (socket) {
      const handleReconnectSync = () => {
        fetchStatusList();
        fetchProfileDetails();
        fetchActiveScheduledAppointment();
      };
      socket.on("connect", handleReconnectSync);
      return () => {
        socket.off("connect", handleReconnectSync);
      };
    }
  }, [socket, fetchStatusList, fetchProfileDetails, fetchActiveScheduledAppointment]);

  const isApptInProgress = Boolean(
    appointmenData?.appointmentId &&
    String(appointmenData?.status || appointmenData?.appointmentStatus || "").toLowerCase() === "inprogress"
  );

  const isApptUpcomingJoinable = Boolean(
    appointmenData?.appointmentId &&
    roleCode !== "consultant" &&
    isUpcomingAppointmentJoinable(appointmenData, userTz)
  );

  const showInProgressBanner = Boolean(
    liveTick >= 0 &&
    token &&
    (isApptInProgress || isApptUpcomingJoinable) &&
    !isLiveCallPath(window.location?.pathname)
  );

  useEffect(() => {
    setIsJoinCall(showInProgressBanner);
  }, [showInProgressBanner]);

  

  useEffect(() => {
    if (speed && speed * 8 < 8) {
      setIsJoinCall(false);
      toast.info("Slow internet connection. Please ensure your connection is stable before starting the consultation.", {
        autoClose: 6000,
      });
      return;
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const profileData = {
    image: profile?.image?.url,
    userName: (profile?.firstName || "") + " " + (profile?.lastName || ""),
    userEmail: profile?.email,
  };

  const menuData = [
    {
      _id: 1,
      name: sideBar?.Home_Label?.[language],
      link: "/",
      click: () => setMenuToggle(false),
    },
    {
      _id: 2,
      name: sideBar?.Find_a_Psychologist_Label?.[language],
      link: `${token ? "/find-psychologist" : "/signin"}`,
      click: () => setMenuToggle(false),
    },
    {
      _id: 5,
      name: sideBar?.Area_of_Intervention_Label?.[language],
      link: "/area-of-intervention",
      click: () => setMenuToggle(false),
    },
    {
      _id: 8,
      name: sideBar?.Blog_Label?.[language],
      link: "/blog",
      click: () => setMenuToggle(false),
    },
    {
      _id: 12,
      name: sideBar?.Contact_us_Label?.[language],
      link: "/contact-us",
      click: () => setMenuToggle(false),
    },
    {
      _id: 13,
      name: token ? "Log Out" : "Sign In",
      link: !token && "/signin",
      click: () => {
        setMenuToggle(false);
        if (token) {
          setShowModal(true);
        }
      },
    },
  ];

  const handleScroll = () => {
    if (menuToggle) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  };

  useEffect(() => {
    function handleScroll() {
      const isScrolled = window.scrollY > 100;
      setScrolled(isScrolled);
    }
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    fetchLanguageList(setLanguageList, setLoaded);
  }, []);

  useEffect(() => {
    handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuToggle]);

  const closeModal = () => setShowModal(false);

  // Incoming Call Now / chat / video ring UI + listeners → IncomingCallHost (global).
  // Keep only status sync + live-call disconnect navigation here.

  useSocket("consultantBusyResponse", (data) => {
    dispatch(updateSingleConsultantStatus({
      _id: data.consultantId,
      onlineStatus: data.isBusy ? "busy" : "online"
    }));
  }, [dispatch]);

  useSocket("userStatusResponse", (data) => {
    if (data && data?.userId) {
      const next =
        data.onlineStatus === "busy" || data.busy === true
          ? "busy"
          : data.onlineStatus === "offline" || data.online === false
            ? "offline"
            : "online";
      dispatch(updateSingleConsultantStatus({
        _id: data.userId,
        onlineStatus: next,
        preserveBusy: next === "online" && !data.onlineStatus,
        readyForAudio: typeof data.readyForAudio === "boolean" ? data.readyForAudio : undefined,
        readyForVideo: typeof data.readyForVideo === "boolean" ? data.readyForVideo : undefined,
      }));
    }
  }, [dispatch]);

  useSocket("consultantReadinessResponse", (data) => {
    if (!data?.userId) return;
    dispatch(
      updateSingleConsultantStatus({
        _id: data.userId,
        readyForAudio: data.readyForAudio,
        readyForVideo: data.readyForVideo,
      }),
    );
  }, [dispatch]);

  useSocket("videoSessionDisconnectResponse", async (data) => {
    if (data?.disconnect !== true) return;
    // Self-leave already navigated via SPA — do NOT full-page reload (that made Leave feel slow)
    if (isSelfLeaving(data?.appointmentId)) return;

    const reason = String(data?.reason || "");
    const free =
      data?.noCharge === true ||
      data?.permissionDenied === true ||
      /permission|media_timeout|USER_MEDIA|GET_USER_MEDIA|NO_MEDIA/i.test(reason);
    const hardEnd = isHardCallDisconnectReason(reason) || free;

    if (!hardEnd) {
      return;
    }

    setIsJoinCall(false);
    localStorage.removeItem("appointmentData");
    localStorage.removeItem("joined");
    localStorage.removeItem("callPageLeaveFlag");
    setAppointmenData(null);
    fetchProfileDetails();

    // Peer left and we are still on the live call page — soft navigate only
    try {
      const path = window.location?.pathname || "";
      if (isLiveCallPath(path)) {
        const apptId = data?.appointmentId;
        const free =
          data?.noCharge === true ||
          data?.permissionDenied === true ||
          /permission|media_timeout|USER_MEDIA|GET_USER_MEDIA|NO_MEDIA/i.test(
            String(data?.reason || ""),
          );
        // SPA navigation is much faster than window.location.assign full reload
        navigate(
          resolveCallExitPath({
            appointmentId: apptId,
            roleCode: localProfileData?.roleCode,
            myId: localProfileData?._id,
            free,
          }),
          { replace: true },
        );
      }
    } catch (_) {
      /* ignore */
    }
  }, [localProfileData?.roleCode, navigate]);

  usePageVisibility(token, socket, profile?._id);

  //---------------online Status--------------------------
  const isOnline = useOnlineStatus();
  const timeoutRef = useRef(null);
  useEffect(() => {
    const emitUserOffline = () => {
      const activeId = userId || profile?._id || localProfileData?._id;
      if (document.visibilityState === "hidden" && socket && activeId) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(
          () => {
            if (!localStorage.getItem("appointmentData")) {
              socket.emit("userOffline", activeId);
            }
          },
          60 * 60 * 1000,
        );
      } else {
        clearTimeout(timeoutRef.current);
      }
    };

    document.addEventListener("visibilitychange", emitUserOffline);

    return () => {
      document.removeEventListener("visibilitychange", emitUserOffline);
      clearTimeout(timeoutRef.current);
    };
  }, [profile, socket, userId, localProfileData?._id]);

  useEffect(() => {
    const intervalId = setInterval(
      () => {
        const activeId = userId || profile?._id || localProfileData?._id;
        if (!socket || !activeId) return;
        const manualOffline = localStorage.getItem("manualOfflineStatus") === "offline";
        if (roleCode === "consultant" && manualOffline) return;
        socket.emit("userPing");
      },
      30 * 1000,
    );

    return () => clearInterval(intervalId);
  }, [socket, profile, roleCode, userId, localProfileData?._id]);

  // Teardown presence when browser tab or window is closed
  useEffect(() => {
    const handleBrowserUnload = (e) => {
      if (e && e.persisted) return;
      const activeId = userId || profile?._id || localProfileData?._id;
      handleTeardownPresence(socket, activeId);
    };

    window.addEventListener("beforeunload", handleBrowserUnload);
    window.addEventListener("pagehide", handleBrowserUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBrowserUnload);
      window.removeEventListener("pagehide", handleBrowserUnload);
    };
  }, [socket, profile?._id, userId, localProfileData?._id]);

  useEffect(() => {
    if (!token) return;

    const checkToken = () => {
      verifyToken().then((res) => {
        if (res && (res?.status === 401 || res?.data?.status === 401)) {
          handleLogOut();
        }
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkToken();
        // Mobile: recover missed socket ring when consultant brings the tab back
        // (browser may have suspended JS / dropped socket while backgrounded).
        if (roleCode === "consultant") {
          fetchProfileDetails();
          // Re-assert online after background if they did not choose Offline
          try {
            const manualOffline = localStorage.getItem("manualOfflineStatus") === "offline";
            if (!manualOffline && socket?.connected) {
              socket.emit("userOnline");
            }
          } catch (_) {
            /* ignore */
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token, handleLogOut, roleCode, fetchProfileDetails, socket]);
  //=========================================================

  // Heartbeat must NOT play ringtone — only incoming-call modals (isOpen / isChatModalOpen) may ring.
  useSocket("appointmentPingResponse", (dataa) => {
    if (
      appointmenData &&
      appointmenData?.appointmentId === dataa?.appointmentId &&
      (String(localProfileData?._id) === String(dataa?.id) ||
        String(localProfileData?._id) === String(dataa?.userId))
    ) {
      setOnCall(true);
    }
  }, [appointmenData, localProfileData?._id]);

  // useEffect(() => {
  //   if (appointmenData && appointmenData?.appointmentId) {
  //     const timer = setTimeout(() => {
  //       appointmentClose({ appointmentId: appointmenData?.appointmentId }).then(
  //         (res) => {
  //           socket &&
  //             socket.emit("videoSessionDisconnect", {
  //               disconnect: true,
  //               appointmentId: appointmenData?.appointmentId,
  //             });

  //           socket &&
  //             socket.emit("chatDisconnect", {
  //               disconnect: true,
  //               appointmentId: appointmenData?.appointmentId,
  //             });

  //           socket &&
  //             localProfileData?.roleCode == "consultant" &&
  //             socket.emit("consultantBusy", {
  //               consultantId: localProfileData?._id,
  //               userId: appointmenData?.callerData?.user?.id,
  //               isBusy: false,
  //             });

  //           socket.emit("userDisconnectFromCallNotify", {
  //             disconnected: false,
  //             sessionId: appointmenData.sessionId,
  //             userId: localProfileData?._id,
  //             appointmentId: appointmenData?.appointmentId,
  //           });
  //         }
  //       );
  //     }, 20 * 1000);

  //     return () => clearTimeout(timer);
  //   }
  // }, [socket, appointmenData]);

  return (
    <>
      <header
        className={classNames(
          "fixed top-0 left-0 w-full z-20 bg-transparent flex flex-col items-center transition-all duration-200",
          isJoinCall
            ? scrolled
              ? "md:h-32 h-28 bg-white shadow-md"
              : "md:h-40 h-32"
            : scrolled
              ? "md:h-24 h-20 bg-white shadow-md"
              : "md:h-32 h-20",
          headerClass,
        )}>
        {!isOnline && (
          <div className="relative w-full bg-red-600 md:px-8 pl-1 pr-4 flex items-center h-10 text-center justify-center">
            <h2 className="text-center">You are offline!</h2>
          </div>
        )}
        {showInProgressBanner && (
          <div className="w-full bg-[#0B1A30] text-white px-4 py-2 sm:py-2.5 z-30 transition-all duration-200">
            <div className="w-full sm:max-w-xl md:max-w-3xl lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl 3xl:max-w-8xl mx-auto flex items-center justify-between gap-3">
              <div className="text-xs sm:text-sm md:text-base text-white font-medium leading-tight sm:leading-normal">
                {`${appointmentLang?.inProgressAppointment?.[language] || "You are having an appointment with"} ${
                  roleCode === "consultant"
                    ? (appointmenData?.callerData?.user?.name || appointmenData?.clientData?.firstName || "Client")
                    : (appointmenData?.callerData?.consultant?.name || appointmenData?.consultantData?.firstName || "Consultant")
                }`}
              </div>
              <div className="flex-shrink-0">
                <Button
                  buttonClasses="!bg-[#9B26B6] hover:!bg-[#871fa1] !text-white font-semibold text-xs sm:text-sm !px-4 sm:!px-6 !py-1.5 sm:!py-2 !h-auto !rounded-md shadow-sm transition-all"
                  buttonLabel={
                    isApptInProgress
                      ? (appointmentLang?.active?.[language] || "Rejoin")
                      : `${langConfig?.joinCall?.[language] || "Join The Call"}`
                  }
                  buttonHasLink={false}
                  buttonType="button"
                  buttonFunction={handleJoinActiveCall}
                  isDisable={loading || secondModalOpen || showDeviceCheck || callConfirmModal || isOpenConfirmationChatModal}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        )}
        <div className=" flex items-center w-full my-auto">
          <div className="w-full px-4 sm:max-w-xl md:max-w-3xl lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl 3xl:max-w-8xl mx-auto flex items-center">
            <div
              className={classNames(
                "relative transition-all duration-200",
                scrolled ? "h-10 md:h-12 lg:h-16" : "h-10 md:h-16 lg:h-20",
              )}>
              <Link to={"/"} className="flex w-full h-full">
                <Image src={Logo} alt={"Prontopsy Logo"} effect={"blur"} className={"object-contain"} />
              </Link>
            </div>
            <div className="flex ml-auto space-x-1 sm:space-x-3">
              <div className="relative">
                <LanguageDropdown
                  lang={language}
                  setLanguages={setLanguages}
                  languageList={languageList}
                  ukFlag={ukFlag}
                  spainFlag={spainFlag}
                  italyFlag={italyFlag}
                  dropdownClasses={screenWidth < 480 ? "min-w-32" : "min-w-32"}
                />
              </div>
              {token ? (
                <div className="flex items-center space-x-2 ml-2">
                  <div>
                    <div className="relative !ml-0">
                      <Button
                        buttonIcon={"fa-solid fa-bell"}
                        buttonIconPosition={"left"}
                        buttonClasses={"h-8 sm:h-10 !px-0 !text-2xl aspect-square !text-prontopsy-blue !bg-transparent"}
                        buttonFunction={() => {
                          setSelectedNotifications([]);
                          token && fetchNotifications();
                          setNotificationToggle(!notificationToggle);
                        }}
                      />
                      {/* -----------------Number of notifications-------------------- */}
                      {totalUnseenNotification > 0 && (
                        <div
                          onClick={() => setNotificationToggle(!notificationToggle)}
                          className="absolute cursor-pointer top-0 right-0 bg-prontopsy-pink w-5 text-white text-xs aspect-square rounded-full flex justify-center items-center">
                          {totalUnseenNotification > 100 ? "99+" : totalUnseenNotification}
                        </div>
                      )}
                    </div>
                    {notificationToggle && (
                      <div className="absolute md:right-32 right-2 top-24 md:w-[400px] w-[350px] bg-white rounded-lg shadow-lg ring-1 ring-gray-200 z-50">
                        {/* Header */}
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-prontopsy-pink text-white rounded-t-md">
                          <h2 className="text-lg font-semibold text-white">Notifications</h2>

                          {notificationData.length > 0 && (
                            <button
                              className={`px-1 py-1 w-[80px] flex items-center justify-center rounded-md ${seenLoader ? "bg-prontopsy-lightblue cursor-not-allowed" : "bg-prontopsy-darkblue"
                                }`}
                              onClick={allSeenLoader ? null : handleAllSeenNotification}
                              disabled={allSeenLoader}>
                              {allSeenLoader ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                              ) : (
                                "Read All"
                              )}
                            </button>
                          )}

                          <button
                            className="text-2xl cursor-pointer"
                            onClick={() => {
                              setNotificationToggle(false);
                              setSelectedNotifications([]);
                            }}>
                            <i className="fa-light fa-xmark"></i>
                          </button>
                        </div>

                        {/* Notification List */}
                        <div className="overflow-y-auto max-h-[calc(100vh-18rem)] bg-white rounded-md shadow-sm">
                          {notificationData.length > 0 ? (
                            notificationData.map((notification) => (
                              <div
                                className={`flex items-center pl-4 pr-2 py-3 transition border-b last:border-none ${selectedNotifications.includes(notification._id) || notification?.seenStatus
                                  ? "bg-gray-200"
                                  : "hover:bg-gray-200"
                                  }`}
                                key={notification._id}>
                                <div
                                  className="flex-1 cursor-pointer"
                                  onClick={async () => {
                                    const isUnseenOrNotSelected = !selectedNotifications.includes(notification?._id);
                                    await apiRequest("put", "/notification/update", {
                                      body: {
                                        id: [notification?._id],
                                      },
                                    });
                                    setNotificationToggle(false);
                                    if (notification?.type === "notification-appointment") {
                                      notification.url !== null &&
                                        setNotificationToggle(false);
                                    } else if (notification?.type === "notification-chat") {
                                      setNotificationToggle(false);
                                    }
                                  }}>
                                  <p className="text-sm text-gray-700">{notification.text}</p>
                                  <div className="text-right mt-1">
                                    <span className="text-xs text-gray-500">
                                      {dayjs(notification.createdAt).format("MMMM D, YYYY h:mm A")}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-gray-600 text-sm text-center">No notifications</div>
                          )}

                          {notificationData.length > 0 && notificationData.length < totalNotificationLength && (
                            <div>
                              <Button
                                buttonLabel="Load More"
                                buttonClasses="w-full rounded-none bg-blue-500 text-white hover:bg-blue-600 transition disabled:bg-gray-300"
                                buttonFunction={() => hasNextPage && setPage((prevPage) => prevPage + 1)}
                                isDisable={notificationLoader}
                                loading={notificationLoader}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-2">
                    <div
                      className="w-8 sm:w-10 aspect-square rounded-full overflow-hidden flex"
                      onClick={() => {
                        dispatch(setTabSelection({}));
                        navigate("/profile");
                      }}>
                      <Image
                        src={profileData && profileData?.image}
                        alt={""}
                        effect={"blur"}
                        className={"!object-cover w-full h-full cursor-pointer"}
                      />
                    </div>
                    <div
                      className="text-sm  font-semibold text-prontopsy-blue hidden md:block cursor-pointer"
                      onClick={() => {
                        dispatch(setTabSelection({}));
                        navigate("/profile");
                      }}>
                      {profileData.userName ? profileData?.userName : ""}
                    </div>
                  </div>
                </div>
              ) : (
                <Button
                  buttonHasLink={true}
                  buttonFunction={() => (window.location.href = "/signin")}
                  buttonLabel={"Signin"}
                  buttonLabelClasses={"md:block hidden"}
                  buttonIcon={"fa-light fa-user-plus"}
                  buttonIconPosition={"left"}
                  buttonClasses={"aspect-square sm:aspect-[unset] px-0 sm:px-4 h-8 sm:h-10 uppercase text-sm hover:!bg-prontopsy-blue"}
                />
              )}

              <Button
                buttonHasLink={false}
                buttonIcon={"fa-solid fa-bars-staggered"}
                buttonIconPosition={"left"}
                buttonClasses={
                  "h-8 sm:h-10 !px-0 text-xl aspect-square !rounded-full !bg-prontopsy-yellow !text-black hover:!bg-prontopsy-blue hover:!text-prontopsy-yellow"
                }
                buttonFunction={() => setMenuToggle(true)}
              />
            </div>
          </div>
        </div>
      </header>

      <NavigationPanel data={menuData} toggle={menuToggle} setToggle={setMenuToggle} />
      <LogoutModal show={showModal} onClose={closeModal} onConfirm={handleLogOut} loaded={loaded} />

      <SecondResume
        isOpen={secondModalOpen}
        setIsOpen={(open) => {
          if (!open && (loading || startCallInFlightRef.current)) {
            return;
          }
          setSecondModalOpen(open);
        }}
        handleContinue={() => {
          if (startCallInFlightRef.current || loading) return;
          setSecondModalOpen(false);

          const appointmentId = appointmenData?.appointmentId;
          const mode = appointmenData?.mode || appointmenData?.service?.mode?.slug || "video";

          // Chat mode skips camera/microphone permission check
          if (String(mode).toLowerCase() === "chat") {
            startInitiateCall(appointmentId);
            return;
          }

          // 2. Then permission popup (PreCallDeviceCheck)
          setShowDeviceCheck(true);
        }}
        loading={loading}
        data={{
          name:
            personName(appointmenData?.consultantData) ||
            appointmenData?.callerData?.consultant?.name ||
            `${appointmenData?.consultantData?.firstName || ""} ${appointmenData?.consultantData?.lastName || ""}`.trim() ||
            "Consultant",
          rate: {
            value:
              appointmenData?.rate?.value ??
              appointmenData?.service?.price ??
              appointmenData?.details?.price ??
              0,
            currency:
              appointmenData?.rate?.currency ||
              appointmenData?.details?.currency ||
              appointmenData?.consultantData?.currencyData?.currency ||
              "",
          },
        }}
        mode={appointmenData?.mode || appointmenData?.service?.mode?.slug || "video"}
        selectedCard={localProfileData?.cardsDetails?.[0] || { last4: "****", brand: "visa" }}
      />

      <PreCallDeviceCheck
        open={showDeviceCheck}
        mode={appointmenData?.mode || appointmenData?.service?.mode?.slug || "video"}
        onReady={({ hasCamera, hasMicrophone }) => {
          const appointmentId = appointmenData?.appointmentId;
          if (appointmentId) {
            saveCallDeviceReadiness(appointmentId, {
              audio: hasMicrophone,
              video: hasCamera,
            });
          }
          setShowDeviceCheck(false);
          startInitiateCall(appointmentId);
        }}
        onCancel={() => {
          setShowDeviceCheck(false);
          startCallInFlightRef.current = false;
          setLoading(false);
        }}
      />

      <JoinCallConfirmationModal
        isOpen={callConfirmModal}
        setIsOpen={setCallConfirmModal}
        callerData={appointmentDetailsData?.callerData || appointmenData?.callerData}
        appointmentData={appointmentDetailsData || appointmenData}
        callId={appointmentDetailsData?.consultantId || appointmenData?.consultantId}
      />

      <JoinChatConfirmModal
        isOpen={isOpenConfirmationChatModal}
        setIsOpen={setIsOpenConfirmationChatModal}
        callerData={appointmentDetailsData?.callerData || appointmenData?.callerData}
        appointmentData={appointmentDetailsData || appointmenData}
        callId={appointmentDetailsData?.consultantId || appointmenData?.consultantId}
      />
      {/* JoinCallModal / JoinChatModal → IncomingCallHost (global, all pages) */}
    </>
  );
};

export default DefaultHeader;
